const TOBACCO = {
  never: 'Never used tobacco',
  current: 'Current smoker, ineligible, complete Study Exit',
};
const NICOTINE = {
  never: 'Never',
  current: 'Current - Daily',
};
const PAIN_MED_YES = 'Yes, record medications on the concomitant medication log';
const BASELINE = 'N/A, baseline exam';
const IMPROVED = 'Deficits have improved from preoperative baseline';
const WORSENED = 'Worsened deficits from preoperative baseline';
const SCORE_VISITS = new Set(['month-3', 'month-6', 'month-12', 'month-24']);
const NEURO_VISITS = SCORE_VISITS;
const TIMEPOINT = {
  'month-1': '1 month',
  'month-3': '3 months',
  'month-6': '6 months',
  'month-12': '12 months',
  'month-24': '24 months',
};
const CERVICAL_SYMPTOMS = new Set([
  'neck_pain',
  'shoulder_pain',
  'shoulder_weakness',
  'upper_extremity_pain',
  'upper_extremity_numbness',
  'upper_extremity_weakness',
]);
const THORACOLUMBAR_SYMPTOMS = new Set([
  'back_pain',
  'lower_extremity_pain',
  'lower_extremity_numbness',
  'lower_extremity_weakness',
]);
const CERVICAL_SENSORY = new Set(['C5', 'C6', 'C7', 'C8', 'T1']);
const INDEX_RULES = [
  { id: 'acdf', pattern: /acdf|anterior cervical discectomy/i, procedure: 'Interbody fusion', approach: 'ACDF' },
  { id: 'alif', pattern: /\balif\b|direct anterior/i, procedure: 'Interbody fusion', approach: 'ALIF' },
  { id: 'tlif', pattern: /\btlif\b/i, procedure: 'Interbody fusion', approach: 'TLIF' },
  { id: 'plif', pattern: /\bplif\b/i, procedure: 'Interbody fusion', approach: 'PLIF' },
  { id: 'llif', pattern: /\b(?:llif|xlif|dlif)\b|direct lateral/i, procedure: 'Interbody fusion', approach: 'DLIF/LLIF' },
  { id: 'olif', pattern: /\bolif\b/i, procedure: 'Interbody fusion', approach: 'OLIF' },
  { id: 'lami', pattern: /laminectomy/i, procedure: 'Direct decompression (bony)/osteotomy', decompression: 'Laminectomy' },
  { id: 'psf', pattern: /\bpsf\b|posterior spinal|posterior fusion/i, procedure: 'Posterior spinal fixation and fusion' },
  { id: 'discectomy', pattern: /discectomy/i, procedure: 'Discectomy', unless: 'acdf' },
  { id: 'corpectomy', pattern: /corpectomy/i, procedure: 'Corpectomy' },
  { id: 'arthroplasty', pattern: /arthroplasty/i, procedure: 'Arthroplasty' },
];

function text(value) {
  if (value == null) return '';
  return String(value).trim();
}

function parseChartDate(value) {
  const raw = text(value);
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) return new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  return null;
}

export function ageInYears(dob, asOf) {
  const born = parseChartDate(dob);
  const anchor = parseChartDate(asOf) || parseChartDate(new Date().toISOString().slice(0, 10));
  if (!born || !anchor || born > anchor) return '';
  let years = anchor.getFullYear() - born.getFullYear();
  if (anchor.getMonth() < born.getMonth()
    || (anchor.getMonth() === born.getMonth() && anchor.getDate() < born.getDate())) {
    years -= 1;
  }
  return years >= 0 ? String(years) : '';
}

function phiRecord(patient) {
  let phi = patient?.phi;
  if (!phi) return null;
  if (typeof phi === 'string') {
    try {
      phi = JSON.parse(phi);
    } catch (error) {
      return null;
    }
  }
  if (!phi || typeof phi !== 'object' || Array.isArray(phi)) return null;
  const nested = phi.patient || phi.Patient;
  if (nested && typeof nested === 'object' && (nested.Sex || nested.sex || nested.Ethnicity || nested.ethnicity || nested.DOB || nested.dob)) {
    return nested;
  }
  return phi;
}

function phiValue(record, names) {
  if (!record) return '';
  const wanted = new Set(names.map((name) => name.toLowerCase()));
  const found = Object.entries(record).find(([key, value]) => wanted.has(String(key).toLowerCase()) && value != null && value !== '');
  return found ? found[1] : '';
}

/** Fill sex, ethnicity, race, and age from the patient record when chart facts omit them. */
export function mergePatientDemographics(chartFacts, patient) {
  const record = phiRecord(patient);
  if (!record) return chartFacts;
  const demo = { ...(chartFacts?.demographics || {}) };
  if (demo.sex == null || demo.sex === '') demo.sex = phiValue(record, ['Sex', 'sex']);
  if (!ethnicityLabel(demo.ethnicity)) demo.ethnicity = phiValue(record, ['Ethnicity', 'ethnicity']);
  const raceMissing = demo.race == null || demo.race === '' || (Array.isArray(demo.race) && !demo.race.length);
  if (raceMissing) {
    const race = phiValue(record, ['Race', 'race']);
    if (Array.isArray(race)) demo.race = race.filter(Boolean);
    else if (text(race)) demo.race = text(race).split(',').map((part) => part.trim()).filter(Boolean);
  }
  if (demo.age == null || demo.age === '') {
    const age = ageInYears(
      phiValue(record, ['DOB', 'dob', 'DateOfBirth']),
      chartFacts?.index_date || patient?.surgery_start_time,
    );
    if (age) demo.age = age;
  }
  return { ...(chartFacts || {}), demographics: demo };
}

function blankValue(value) {
  return value == null || value === '' || (Array.isArray(value) && !value.length);
}

function fillBlankFields(state, forms) {
  const nextForms = { ...(state?.forms || {}) };
  Object.entries(forms || {}).forEach(([key, form]) => {
    const prior = nextForms[key] || { values: {}, repeats: {} };
    const values = { ...(prior.values || {}) };
    Object.entries(form?.values || {}).forEach(([field, value]) => {
      if (blankValue(values[field])) values[field] = value;
    });
    nextForms[key] = { ...prior, values };
  });
  return { ...state, forms: nextForms };
}

const ALLIANCE_REPEAT_FIELDS = [
  'alliance.ethnicity',
  'alliance.race',
  'alliance.ethnicity.other',
  'alliance.race.other',
];

/** The ethnicity form shows the first repeating row as field#0. */
function promoteAllianceRepeat(state) {
  const key = 'ethnicity::case';
  const form = state?.forms?.[key];
  if (!form?.values) return state || {};
  const values = { ...form.values };
  let changed = false;
  ALLIANCE_REPEAT_FIELDS.forEach((field) => {
    const indexed = `${field}#0`;
    if (blankValue(values[indexed]) && !blankValue(values[field])) {
      values[indexed] = values[field];
      changed = true;
    }
  });
  if (!changed) return state;
  return { ...state, forms: { ...state.forms, [key]: { ...form, values } } };
}

/** Write sex, ethnicity, race, and age onto a chart without clearing answers already entered. */
export function stampPatientDemographics(state, patient, study) {
  const base = state || {};
  const facts = mergePatientDemographics(null, patient);
  const demo = facts?.demographics || {};
  const hasDemo = demo.sex || demo.ethnicity || (Array.isArray(demo.race) && demo.race.length) || (demo.age != null && demo.age !== '');
  if (!hasDemo) return study === 'alliance' ? promoteAllianceRepeat(base) : base;
  const applied = applyChartFacts(facts, {
    arm: base.arm || 'Cervical',
    region: base.region || '',
    indexDate: text(patient?.surgery_start_time).slice(0, 10),
  });
  const forms = study === 'alliance' ? applied.alliance?.forms : applied.compass?.forms;
  return fillBlankFields(base, forms);
}

function trimNumber(value, places) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  const factor = 10 ** places;
  const rounded = Math.round(number * factor) / factor;
  return String(rounded);
}

function yesNo(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '';
}

function historyPrefix(arm) {
  if (arm === 'Cervical') return 'cervical';
  if (arm === 'Thoracolumbar') return 'thoracolumbar';
  return 'adult_spinal_deformity';
}

function symptomPrefix(arm, region) {
  if (arm === 'Cervical' || region === 'Cervical') return 'cervical';
  return 'thoracolumbar';
}

function put(forms, formId, visitId, values, repeats) {
  const filled = {};
  Object.entries(values || {}).forEach(([key, value]) => {
    if (value === '' || value == null) return;
    if (Array.isArray(value) && !value.length) return;
    filled[key] = value;
  });
  const extraRepeats = repeats || {};
  if (!Object.keys(filled).length && !Object.keys(extraRepeats).length) return;
  const id = `${formId}::${visitId || 'case'}`;
  const prior = forms[id] || { values: {}, repeats: {} };
  forms[id] = {
    values: { ...prior.values, ...filled },
    repeats: { ...prior.repeats, ...extraRepeats },
  };
}

function sexLabel(value) {
  const token = text(value).toUpperCase();
  if (token === 'F' || token === 'FEMALE') return 'Female';
  if (token === 'M' || token === 'MALE') return 'Male';
  return '';
}

function ethnicityLabel(value) {
  const token = text(value).toLowerCase();
  if (!token) return '';
  if (token.includes('not hispanic')) return 'Not Hispanic or Latino';
  if (token.includes('hispanic')) return 'Hispanic or Latino';
  return '';
}

function racePair(value) {
  const token = text(value).toLowerCase();
  if (!token) return null;
  if (token.includes('black') || token.includes('african')) {
    return ['Black or African-American', 'Black or African American'];
  }
  if (token.includes('white') || token.includes('caucasian')) return ['Caucasian', 'White'];
  if (token.includes('asian')) return ['Asian', 'Asian'];
  if (token.includes('alaska') || token.includes('american indian')) {
    return ['American Indian or Alaska Native', 'American Indian or Alaska Native'];
  }
  if (token.includes('hawaiian') || token.includes('pacific')) {
    return ['Native Hawaiian or other Pacific Islander', 'Native Hawaiian or Other Pacific Islander'];
  }
  if (token === 'other') return ['Other', 'Other'];
  return null;
}

function heightParts(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  if (number > 90) {
    return { inches: trimNumber(number / 2.54, 1), amount: trimNumber(number, 1), unit: 'cm' };
  }
  return { inches: trimNumber(number, 1), amount: trimNumber(number, 1), unit: 'in' };
}

function weightPounds(value, heightRaw) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';
  const pounds = Number(heightRaw) > 90 && number < 140 ? number * 2.20462 : number;
  return trimNumber(pounds, 1);
}

function levelSpan(value) {
  const found = [...text(value).matchAll(/\b([CTLS])(\d{1,2})\b/gi)].map((match) => (
    `${match[1].toUpperCase()}${match[2]}`
  ));
  if (!found.length) return null;
  return { from: found[0], to: found[found.length - 1] };
}

function assignPain(hpi) {
  const pain = { ...(hpi?.pain || {}) };
  if (!pain.unspecified || pain.neck || pain.arm || pain.back || pain.leg) return pain;
  const fromComplaint = {
    'Pain, neck': 'neck',
    'Pain, arm': 'arm',
    'Pain, back': 'back',
    'Pain, leg': 'leg',
  }[hpi.primary_complaint];
  const fromSymptom = {
    neck_pain: 'neck',
    shoulder_pain: 'arm',
    upper_extremity_pain: 'arm',
    back_pain: 'back',
    lower_extremity_pain: 'leg',
  };
  const symptom = (hpi.symptoms || []).map((item) => fromSymptom[item.key]).find(Boolean);
  const site = fromComplaint || symptom;
  if (site) pain[site] = pain.unspecified;
  return pain;
}

function diagnosisKey(arm, tag, description) {
  if (arm === 'Adult Spinal Deformity') {
    if (tag === 'degenerative_scoliosis') return 'dx.adult_spinal_deformity.degenerative_scoliosis';
    if (tag === 'scoliosis') return 'dx.adult_spinal_deformity.scoliosis';
    if (tag === 'lordosis') return 'dx.adult_spinal_deformity.lordosis';
    return '';
  }
  const prefix = arm === 'Thoracolumbar' ? 'thoracolumbar' : 'cervical';
  if (tag === 'ddd') {
    if (prefix === 'cervical') return 'dx.cervical.ddd';
    if (/grade\s*(i|1)\b/i.test(description || '')) return 'dx.thoracolumbar.ddd_up_to_grade_1_spondylolisthesis';
    return 'dx.thoracolumbar.ddd_unspecified_spondylolisthesis';
  }
  if (tag === 'spondylolysis') return prefix === 'thoracolumbar' ? 'dx.thoracolumbar.spondylolysis' : '';
  if (tag === 'tumor') return prefix === 'cervical' ? 'dx.cervical.tumors' : 'dx.thoracolumbar.neoplastic_disease';
  if (tag === 'pseudoarthrosis' || tag === 'failed_previous_fusion') {
    return prefix === 'cervical' ? 'dx.cervical.pseudoarthrosis' : 'dx.thoracolumbar.failed_previous_fusion';
  }
  if (tag === 'facet_instability') return prefix === 'cervical' ? 'dx.cervical.facet_instability' : '';
  if (tag === 'spinal_stenosis') return `dx.${prefix}.spinal_stenosis`;
  if (tag === 'spondylolisthesis') return `dx.${prefix}.spondylolisthesis`;
  if (tag === 'trauma') return `dx.${prefix}.trauma`;
  return '';
}

function indication(diagnoses) {
  const tags = new Set(diagnoses.flatMap((row) => row.tags || []));
  const blob = diagnoses.map((row) => row.description || '').join(' ').toLowerCase();
  const result = {};
  if (tags.has('tumor')) result.primary = 'Tumor case';
  else if (tags.has('trauma')) result.primary = /patholog/.test(blob) ? 'Fracture - pathological' : 'Fracture - traumatic';
  else if (tags.has('osteomyelitis')) result.primary = 'Inflammation/infection';
  else if (tags.has('degenerative_scoliosis') || tags.has('scoliosis') || tags.has('lordosis')) {
    result.primary = 'Deformity';
    const types = [];
    if (tags.has('scoliosis') || tags.has('degenerative_scoliosis')) types.push('Scoliosis');
    if (/kyphosis/.test(blob)) types.push('Kyphosis');
    if (types.length) result.deformity = types;
  } else if (tags.has('pseudoarthrosis') || tags.has('failed_previous_fusion')) {
    result.primary = 'Fusion failure/revision surgery';
  } else if (tags.has('spondylolisthesis') && !/degenerative/.test(blob)) {
    result.primary = 'Spondylolisthesis (non-degenerative)';
  } else if (
    tags.has('spinal_stenosis')
    || tags.has('ddd')
    || tags.has('spondylolisthesis')
    || tags.has('facet_instability')
    || tags.has('spondylolysis')
  ) {
    result.primary = 'Degenerative disease';
    if (/central stenosis/.test(blob)) result.degen = 'Central stenosis';
    else if (/foraminal/.test(blob)) result.degen = 'Foraminal stenosis';
    else if (/lateral stenosis/.test(blob)) result.degen = 'Lateral stenosis';
    else if (tags.has('ddd')) result.degen = 'Degenerative disc disease';
    else if (/degenerative spondylolisthesis/.test(blob) || (tags.has('spondylolisthesis') && /degenerative/.test(blob))) {
      result.degen = 'Degenerative spondylolisthesis';
    } else if (/herniat/.test(blob)) result.degen = 'Disc herniation';
    else if (tags.has('facet_instability')) result.degen = 'Facet joint arthrosis';
  }
  return result;
}

function caseType(diagnoses, procedureText) {
  const parts = new Set(diagnoses.map((row) => row.body_part).filter(Boolean));
  const blob = text(procedureText).toLowerCase();
  if (/cerv|\bc\d/.test(blob)) parts.add('cervical');
  if (/lumb|thorac|sacr|\b[tls]\d/.test(blob)) parts.add('thoracolumbar');
  const cervical = parts.has('cervical');
  const thoracolumbar = parts.has('thoracolumbar');
  if (cervical && thoracolumbar) return 'Both';
  if (cervical) return 'Cervical';
  if (thoracolumbar) return 'Thoracic, lumbar, and/or sacral';
  return '';
}

function indexProcedures(procedureText) {
  const matched = new Set();
  const rows = [];
  INDEX_RULES.forEach((rule) => {
    if (rule.unless && matched.has(rule.unless)) return;
    if (!rule.pattern.test(procedureText || '')) return;
    matched.add(rule.id);
    const key = `${rule.procedure}|${rule.approach || ''}|${rule.decompression || ''}`;
    if (rows.some((row) => row.key === key)) return;
    rows.push({ key, ...rule });
  });
  return rows;
}

function priorTerm(label) {
  const token = text(label).toLowerCase();
  if (!token) return null;
  if (token === 'acdf' || /interbody|tlif|alif|plif|llif|olif|xlif/.test(token)) return { term: 'Interbody fusion' };
  if (token === 'psf' || /posterior/.test(token)) return { term: 'Posterior spinal fixation and fusion' };
  if (token.startsWith('lami')) return { term: 'Other', other: 'Laminectomy' };
  if (token === 'discectomy') return { term: 'Discectomy' };
  if (token === 'corpectomy') return { term: 'Corpectomy' };
  if (token === 'arthroplasty') return { term: 'Arthroplasty' };
  return { term: 'Other', other: label };
}

function medAnswer(flag, listed) {
  if (flag) return 'Yes';
  if (listed) return 'No';
  return '';
}

function applyDemographics(compass, alliance, facts) {
  const demo = facts.demographics || {};
  const sex = sexLabel(demo.sex);
  const age = demo.age != null && demo.age !== '' ? trimNumber(demo.age, 0) : '';
  const height = heightParts(demo.height);
  const weight = weightPounds(demo.weight, demo.height);
  const bmi = demo.bmi != null && demo.bmi !== '' ? trimNumber(demo.bmi, 1) : '';
  const ethnicity = ethnicityLabel(demo.ethnicity);
  const raceValues = Array.isArray(demo.race) ? demo.race : (text(demo.race) ? [demo.race] : []);
  const races = raceValues.map(racePair).filter(Boolean);
  if (age) {
    compass.age_at_surgery = age;
    alliance['alliance.baseline.age'] = age;
    alliance['alliance.baseline.age_90_or_older'] = Number(age) >= 90 ? 'Yes' : 'No';
  }
  if (sex) {
    compass.sex = sex;
    alliance['alliance.baseline.sex_at_birth'] = sex;
  }
  if (height) {
    compass.height_inches = height.inches;
    alliance['alliance.baseline.height'] = height.amount;
    alliance['alliance.baseline.height_unit'] = height.unit;
  }
  if (weight) {
    compass.weight_pounds = weight;
    alliance['alliance.baseline.weight'] = weight;
    alliance['alliance.baseline.weight_unit'] = 'lb';
  }
  if (bmi) {
    compass.bmi = bmi;
    alliance['alliance.baseline.bmi'] = bmi;
  }
  if (ethnicity) {
    compass.ethnicity = ethnicity;
    alliance['alliance.ethnicity#0'] = ethnicity;
  }
  if (races.length) {
    compass.race = races.map((pair) => pair[0]);
    alliance['alliance.race#0'] = races[0][1];
  }
  const smoking = text(demo.smoking).toUpperCase();
  if (smoking === 'YES') {
    compass.tobacco_history = TOBACCO.current;
    alliance['alliance.baseline.nicotine.status'] = NICOTINE.current;
  } else if (smoking === 'NO') {
    compass.tobacco_history = TOBACCO.never;
    alliance['alliance.baseline.nicotine.status'] = NICOTINE.never;
  }
}

function applySocial(alliance, social) {
  if (!social) return;
  if (social.education) alliance['alliance.baseline.education'] = social.education;
  if (social.retired === true) alliance['alliance.baseline.employment'] = ['Retired'];
}

function applyDiagnoses(history, arm, diagnoses) {
  (diagnoses || []).forEach((row) => {
    const levels = row.level_from ? { from: row.level_from, to: row.level_to || row.level_from } : null;
    (row.tags || []).forEach((tag) => {
      const key = diagnosisKey(arm, tag, row.description);
      if (!key) return;
      history[key] = 'Yes';
      if (levels?.from) history[`${key}_level_from`] = levels.from;
      if (levels?.to) history[`${key}_level_to`] = levels.to;
    });
  });
}

function applyTreatment(history, spine, arm, hpi) {
  if (!hpi) return;
  const prefix = historyPrefix(arm);
  const stated = [hpi.physical_therapy, hpi.injections, hpi.prior_surgery].filter((value) => value != null);
  const anyYes = stated.some(Boolean) || (hpi.prior_surgeries || []).length > 0;
  if (anyYes) history[`hx.${prefix}.previous_treatment`] = 'Yes';
  else if (stated.length && stated.every((value) => value === false)) history[`hx.${prefix}.previous_treatment`] = 'No';
  if (hpi.physical_therapy === true) history[`tx.${prefix}.physical_therapy`] = true;
  if (hpi.injections === true) history[`tx.${prefix}.spinal_injections`] = true;
  const surgeries = hpi.prior_surgeries || [];
  if (hpi.prior_surgery === true || surgeries.length) {
    history[`tx.${prefix}.previous_spine_surgery`] = true;
    spine['alliance.spine_history.any'] = 'Yes';
  } else if (hpi.prior_surgery === false) {
    spine['alliance.spine_history.any'] = 'No';
  }
  surgeries.slice(0, 3).forEach((row, index) => {
    const slot = index + 1;
    const base = `prior_surg.${prefix}.${slot}`;
    if (row.date) history[`${base}.date`] = row.date;
    if (row.level_from) history[`${base}.level_from`] = row.level_from;
    if (row.level_to) history[`${base}.level_to`] = row.level_to;
    history[`${base}.procedure_summary`] = row.summary || row.procedure || '';
    const term = priorTerm(row.procedure);
    if (!term) return;
    spine[`alliance.spine_history.term#${index}`] = term.term;
    if (term.other) spine[`alliance.spine_history.term_other#${index}`] = term.other;
    if (row.date) spine[`alliance.spine_history.date#${index}`] = row.date;
    if (row.level_from) spine[`alliance.spine_history.level_start#${index}`] = row.level_from;
    if (row.level_to) spine[`alliance.spine_history.level_end#${index}`] = row.level_to;
  });
}

function applySymptoms(values, prefix, hpi, { followUp, date }) {
  if (!hpi) return;
  const allowed = prefix === 'cervical' ? CERVICAL_SYMPTOMS : THORACOLUMBAR_SYMPTOMS;
  const presence = followUp && hpi.better_or_worse === 'worse' ? 'Worse' : 'Present';
  let any = false;
  (hpi.symptoms || []).forEach((item) => {
    if (!allowed.has(item.key)) return;
    any = true;
    values[`sx.${prefix}.${item.key}_presence`] = presence;
    if (item.location) values[`sx.${prefix}.${item.key}_location`] = item.location;
  });
  (hpi.other_symptoms || []).slice(0, 4).forEach((label, index) => {
    any = true;
    values[`sx.${prefix}.other_${index + 1}_description`] = label;
    values[`sx.${prefix}.other_${index + 1}_presence`] = presence;
  });
  if (prefix === 'thoracolumbar' && hpi.bowel_bladder_normal === true) {
    values['sx.thoracolumbar.bladder_bowel_incontinence_presence'] = 'None';
  }
  if (any) {
    values[`sx.${prefix}.review_performed`] = 'Yes';
    if (date) values[`sx.${prefix}.review_date`] = date;
    if (!followUp) values[`sx.${prefix}.new_or_worse`] = BASELINE;
  }
  if (followUp && hpi.better_or_worse === 'worse') values[`sx.${prefix}.new_or_worse`] = 'Yes';
  if (followUp && hpi.better_or_worse === 'improved') values[`sx.${prefix}.new_or_worse`] = 'No';
}

function applyScores(values, hpi, date) {
  if (!hpi) return;
  const pain = assignPain(hpi);
  if (pain.neck) values['prom.vas_neck.score'] = pain.neck;
  if (pain.arm) values['prom.vas_arm.score'] = pain.arm;
  if (pain.back) values['prom.vas_back.score'] = pain.back;
  if (pain.leg) values['prom.vas_leg.score'] = pain.leg;
  if (hpi.ndi) values['prom.ndi.total'] = String(hpi.ndi);
  if (hpi.odi) values['prom.odi.total'] = String(hpi.odi);
  if (!date) return;
  if (pain.neck) values['prom.vas_neck.completion_date'] = date;
  if (pain.arm) values['prom.vas_arm.completion_date'] = date;
  if (pain.back) values['prom.vas_back.completion_date'] = date;
  if (pain.leg) values['prom.vas_leg.completion_date'] = date;
  if (hpi.ndi) values['prom.ndi.completion_date'] = date;
  if (hpi.odi) values['prom.odi.completion_date'] = date;
}

function applyAllianceScores(values, hpi, date, keyPrefix) {
  if (!hpi || !keyPrefix) return;
  const pain = assignPain(hpi);
  const sites = ['neck', 'arm', 'back', 'leg'].filter((site) => pain[site]);
  if (sites.length) {
    values[`${keyPrefix}.vas.completed`] = 'Yes';
    values[`${keyPrefix}.vas.scale`] = '0 to 10';
    if (date) values[`${keyPrefix}.vas.date`] = date;
    sites.forEach((site) => {
      values[`${keyPrefix}.vas.${site}`] = pain[site];
    });
  }
  if (hpi.ndi) {
    values[`${keyPrefix}.ndi.completed`] = 'Yes';
    values[`${keyPrefix}.ndi.score`] = String(hpi.ndi);
    if (date) values[`${keyPrefix}.ndi.date`] = date;
  }
  if (hpi.odi) {
    values[`${keyPrefix}.odi.completed`] = 'Yes';
    values[`${keyPrefix}.odi.score`] = String(hpi.odi);
    if (date) values[`${keyPrefix}.odi.date`] = date;
  }
}

function applyNeuro(values, prefix, exam, date) {
  if (!exam?.performed) return;
  values[`neuro.${prefix}.performed`] = 'Yes';
  if (date) values[`neuro.${prefix}.assessment_date`] = date;
  const motor = Object.entries(exam.motor || {}).filter(([key]) => key.startsWith(`${prefix}.`));
  if (motor.length) {
    values[`neuro.${prefix}.motor_overall`] = 'Abnormal';
    motor.forEach(([key, grade]) => {
      values[`neuro.${prefix}.motor_${key.slice(prefix.length + 1)}`] = grade;
    });
  } else if (exam.motor_overall) {
    values[`neuro.${prefix}.motor_overall`] = 'All normal';
  }
  Object.entries(exam.reflexes || {}).forEach(([key, grade]) => {
    if (!key.startsWith(`${prefix}.`)) return;
    values[`neuro.${prefix}.reflex_${key.slice(prefix.length + 1)}`] = grade;
  });
  Object.entries(exam.sensation || {}).forEach(([key, grade]) => {
    const level = key.split('_')[0].toUpperCase();
    const side = key.split('_').slice(1).join('_');
    const cervical = CERVICAL_SENSORY.has(level);
    if (prefix === 'cervical' ? !cervical : cervical) return;
    values[`neuro.${prefix}.sensory_${level.toLowerCase()}_${side}`] = grade;
  });
  if (prefix !== 'cervical') return;
  if (exam.hoffman) {
    values['neuro.cervical.hoffmans_right'] = exam.hoffman;
    values['neuro.cervical.hoffmans_left'] = exam.hoffman;
  }
  if (exam.clonus) {
    values['neuro.cervical.ankle_clonus_right'] = exam.clonus;
    values['neuro.cervical.ankle_clonus_left'] = exam.clonus;
  }
  if (exam.spurling?.right) values['neuro.cervical.spurlings_right'] = exam.spurling.right;
  if (exam.spurling?.left) values['neuro.cervical.spurlings_left'] = exam.spurling.left;
}

function applyAllianceNeuro(values, exam, date, { followUp, preop }) {
  if (!exam?.performed) return;
  const prefix = followUp ? 'alliance.visit.neuro' : 'alliance.baseline.neuro';
  values[`${prefix}.completed`] = 'Yes';
  if (date) values[`${prefix}.date`] = date;
  if (followUp) {
    const course = followUp.better_or_worse;
    const status = (hadDeficit) => {
      if (course === 'improved' && hadDeficit) return IMPROVED;
      if (course === 'worse' && hadDeficit) return WORSENED;
      return '';
    };
    const focal = status(preop?.focal_deficit === true);
    if (focal) values['alliance.visit.neuro.focal'] = focal;
    const motor = status(preop?.motor_overall === 'Abnormal');
    const sensory = status(preop?.sensory_abnormal === true);
    const reflex = status(preop?.reflex_disrupted === true);
    if (motor) values['alliance.visit.neuro.motor'] = motor;
    if (sensory) values['alliance.visit.neuro.sensory'] = sensory;
    if (reflex) values['alliance.visit.neuro.reflex'] = reflex;
    return;
  }
  if (exam.focal_deficit != null) values['alliance.baseline.neuro.focal_deficit'] = yesNo(exam.focal_deficit);
  if (exam.reflex_disrupted != null) values['alliance.baseline.neuro.reflex'] = yesNo(exam.reflex_disrupted);
  if (exam.sensory_abnormal != null) values['alliance.baseline.neuro.sensory'] = exam.sensory_abnormal ? 'Abnormal' : 'Normal';
  if (exam.motor_overall === 'Abnormal') values['alliance.baseline.neuro.motor'] = 'Abnormal';
  if (exam.motor_overall === 'All normal') values['alliance.baseline.neuro.motor'] = 'Normal';
}

function applySystems(examValues, systems) {
  (systems?.normal || []).forEach((system) => {
    examValues[`pe_${system}_clinically_relevant`] = 'No';
  });
  (systems?.relevant || []).forEach((item) => {
    if (!item?.system) return;
    examValues[`pe_${item.system}_clinically_relevant`] = 'Yes';
    if (item.description) examValues[`pe_${item.system}_description`] = item.description;
  });
}

function applyMedications(exam, medsForm, allianceBaseline, allianceDischarge, medications) {
  const meds = medications || {};
  const pain = Array.isArray(meds.pain) ? meds.pain.slice(0, 8) : [];
  pain.forEach((row, index) => {
    medsForm[`med.medication_name#${index}`] = row.name;
    if (row.dose) medsForm[`med.dose#${index}`] = row.dose;
    if (row.dose_units) medsForm[`med.dose_units#${index}`] = row.dose_units;
    if (row.frequency) medsForm[`med.frequency#${index}`] = row.frequency;
    if (row.route) medsForm[`med.route#${index}`] = row.route;
  });
  if (pain.length) exam.pain_med_for_spine_last_30_days = PAIN_MED_YES;
  const listed = meds.listed === true;
  const vitamin = medAnswer(meds.vitamin_d, listed);
  const hormone = medAnswer(meds.hormone_replacement, listed);
  const diabetes = medAnswer(meds.diabetes, listed);
  const opiates = medAnswer(meds.opiates, listed);
  if (vitamin) {
    allianceBaseline['alliance.baseline.vitamin_d_supplement'] = vitamin;
    allianceDischarge['alliance.discharge.vitamin_d'] = vitamin;
  }
  if (hormone) {
    allianceBaseline['alliance.baseline.hormone_replacement'] = hormone;
    allianceDischarge['alliance.discharge.hormone'] = hormone;
  }
  if (diabetes) {
    allianceBaseline['alliance.baseline.diabetes_meds'] = diabetes;
    allianceDischarge['alliance.discharge.diabetes'] = diabetes;
  }
  if (opiates) {
    allianceBaseline['alliance.baseline.opiates'] = opiates;
    allianceDischarge['alliance.discharge.opiates'] = opiates;
  }
  return pain.length;
}

export function applyChartFacts(chartFacts, {
  arm = 'Cervical',
  region = '',
  procedureText = '',
  indexDate = '',
  indexTime = '',
} = {}) {
  const facts = chartFacts || {};
  const compassForms = {};
  const allianceForms = {};
  const visits = {};
  const prefix = symptomPrefix(arm, region);
  const demo = {};
  const tobacco = {};
  const allianceDemo = {};
  const ethnicity = {};
  applyDemographics(demo, allianceDemo, facts);
  if (demo.tobacco_history) {
    tobacco.tobacco_history = demo.tobacco_history;
    delete demo.tobacco_history;
  }
  if (allianceDemo['alliance.ethnicity#0']) {
    ethnicity['alliance.ethnicity#0'] = allianceDemo['alliance.ethnicity#0'];
    delete allianceDemo['alliance.ethnicity#0'];
  }
  if (allianceDemo['alliance.race#0']) {
    ethnicity['alliance.race#0'] = allianceDemo['alliance.race#0'];
    delete allianceDemo['alliance.race#0'];
  }

  const history = {};
  const spine = {};
  const therapies = [];
  const hpi = facts.preop?.hpi || {};
  if (hpi.symptom_description) history[`hx.${historyPrefix(arm)}.symptom_description`] = hpi.symptom_description;
  if (hpi.onset) history[`hx.${historyPrefix(arm)}.symptom_onset`] = hpi.onset;
  applyDiagnoses(history, arm, facts.diagnoses);
  applyTreatment(history, spine, arm, facts.preop?.hpi);
  if (hpi.physical_therapy === true) therapies.push('Physical therapy');
  if (hpi.injections === true) therapies.push('Steroid injections');
  const historyAlliance = {};
  if (therapies.length) historyAlliance['alliance.history.therapies'] = therapies;
  if (facts.medications?.diabetes_history) {
    historyAlliance['alliance.history.term#0'] = 'Diabetes';
  }

  const indicationValues = {};
  const chosen = indication(facts.diagnoses || []);
  const kind = caseType(facts.diagnoses || [], procedureText);
  if (kind) indicationValues['alliance.indication.case_type'] = kind;
  if (chosen.primary) indicationValues['alliance.indication.primary'] = chosen.primary;
  if (chosen.deformity) indicationValues['alliance.indication.deformity.type'] = chosen.deformity;
  if (chosen.degen) indicationValues['alliance.indication.degen.type'] = chosen.degen;
  const myelopathy = (facts.diagnoses || []).some((row) => row.myelopathy) || hpi.myelopathy === true;
  if (myelopathy) indicationValues['alliance.indication.myelopathy'] = 'Yes';
  if (hpi.primary_complaint) indicationValues['alliance.indication.primary_complaint'] = hpi.primary_complaint;
  const injury = yesNo(hpi.auto_or_injury);
  if (injury) indicationValues['alliance.indication.auto_or_injury'] = injury;

  const preopDate = facts.exam_date || facts.preop?.date || '';
  const symptoms = {};
  const proms = {};
  applySymptoms(symptoms, prefix, facts.preop?.hpi, { followUp: false, date: preopDate });
  applyScores(proms, facts.preop?.hpi, preopDate);
  applyNeuro(symptoms, prefix, facts.preop?.exam, preopDate);
  applyAllianceScores(allianceDemo, facts.preop?.hpi, preopDate, 'alliance.baseline');
  applyAllianceNeuro(allianceDemo, facts.preop?.exam, preopDate, {});
  if (preopDate) visits['pre-op'] = { date: preopDate };

  const exam = {};
  if (preopDate || (facts.systems?.normal || []).length || (facts.systems?.relevant || []).length) {
    exam.physical_exam_completed = 'Yes';
  }
  applySystems(exam, facts.systems);

  const meds = {};
  const discharge = {};
  const wrotePain = applyMedications(exam, meds, allianceDemo, discharge, facts.medications);

  const operative = {};
  const surgeryDate = facts.index_date || indexDate;
  if (surgeryDate) {
    operative.surgery_date = surgeryDate;
    operative['alliance.index.date'] = surgeryDate;
  }
  const clock = indexTime && indexTime !== '00:00' ? indexTime : '';
  if (clock) operative['alliance.index.start_time'] = clock;
  const levels = levelSpan(procedureText);
  if (levels) {
    operative['procedure.levels_from'] = levels.from;
    operative['procedure.levels_to'] = levels.to;
  }
  if (/percutaneous|minimally invasive|\bmis\b/i.test(procedureText)) {
    operative['procedure.open_or_minimally_invasive'] = 'Minimally invasive';
    operative['alliance.index.access'] = 'Minimally invasive';
  }
  const procedures = indexProcedures(procedureText);
  procedures.forEach((row, index) => {
    operative[`alliance.index.procedure#${index}`] = row.procedure;
    if (row.approach) operative[`alliance.index.approach#${index}`] = row.approach;
    if (row.decompression) operative[`alliance.index.decompression#${index}`] = row.decompression;
    if (levels) {
      operative[`alliance.index.level_start#${index}`] = levels.from;
      operative[`alliance.index.level_end#${index}`] = levels.to;
    }
  });

  (facts.followups || []).forEach((visit) => {
    if (visit.compass_visit) {
      const followSymptoms = {};
      const followProms = {};
      applySymptoms(followSymptoms, prefix, visit.hpi, { followUp: true, date: visit.date });
      applyScores(followProms, visit.hpi, visit.date);
      applyNeuro(followSymptoms, prefix, visit.exam, visit.date);
      put(compassForms, 'follow-symptoms', visit.compass_visit, followSymptoms);
      put(compassForms, 'follow-proms', visit.compass_visit, followProms);
      visits[visit.compass_visit] = { date: visit.date };
    }
    if (!visit.alliance_visit) return;
    const follow = {
      'alliance.visit.occurred': 'Yes',
      'alliance.visit.date': visit.date,
      'alliance.visit.timepoint': TIMEPOINT[visit.alliance_visit] || '',
    };
    if (SCORE_VISITS.has(visit.alliance_visit)) {
      applyAllianceScores(follow, visit.hpi, visit.date, 'alliance.visit');
    }
    if (NEURO_VISITS.has(visit.alliance_visit)) {
      applyAllianceNeuro(follow, visit.exam, visit.date, {
        followUp: visit.hpi || {},
        preop: facts.preop?.exam,
      });
    }
    put(allianceForms, 'visit', visit.alliance_visit, follow);
  });

  put(compassForms, 'demographics', 'pre-op', demo);
  put(compassForms, 'tobacco', 'pre-op', tobacco);
  put(compassForms, 'history', 'pre-op', history);
  put(compassForms, 'symptoms', 'pre-op', symptoms);
  put(compassForms, 'proms', 'pre-op', proms);
  put(compassForms, 'exam', 'pre-op', exam);
  put(compassForms, 'operative', '', {
    surgery_date: operative.surgery_date,
    'procedure.levels_from': operative['procedure.levels_from'],
    'procedure.levels_to': operative['procedure.levels_to'],
    'procedure.open_or_minimally_invasive': operative['procedure.open_or_minimally_invasive'],
  });
  if (wrotePain) {
    put(compassForms, 'meds', '', meds, { concomitant_medication: (facts.medications?.pain || []).length });
  }
  put(allianceForms, 'indication', '', indicationValues);
  put(allianceForms, 'baseline', '', allianceDemo);
  put(allianceForms, 'ethnicity', '', ethnicity);
  applySocial(allianceDemo, facts.social);
  put(allianceForms, 'baseline', '', {
    'alliance.baseline.education': allianceDemo['alliance.baseline.education'],
    'alliance.baseline.employment': allianceDemo['alliance.baseline.employment'],
  });
  put(
    allianceForms,
    'history',
    '',
    historyAlliance,
    facts.medications?.diabetes_history ? { medical_history: 1 } : null,
  );
  put(
    allianceForms,
    'spine-history',
    '',
    spine,
    (facts.preop?.hpi?.prior_surgeries || []).length > 1
      ? { prior_spine_surgery: facts.preop.hpi.prior_surgeries.length }
      : null,
  );
  const allianceOperative = {};
  Object.entries(operative).forEach(([key, value]) => {
    if (key.startsWith('alliance.index.') || key === 'alliance.index.date') allianceOperative[key] = value;
  });
  ['alliance.index.date', 'alliance.index.start_time', 'alliance.index.access'].forEach((key) => {
    if (operative[key]) allianceOperative[key] = operative[key];
  });
  Object.keys(operative).forEach((key) => {
    if (key.startsWith('alliance.index.')) allianceOperative[key] = operative[key];
  });
  put(
    allianceForms,
    'operative',
    '',
    allianceOperative,
    procedures.length > 1 ? { index_procedure: procedures.length } : null,
  );
  put(allianceForms, 'discharge', '', discharge);

  return {
    compass: { forms: compassForms, visits },
    alliance: { forms: allianceForms },
    painMedicationCount: wrotePain ? (facts.medications?.pain || []).length : 0,
  };
}
